import { Layout } from 'layouts/Layout'
import {
  SectionTitle,
  MyProfileBody,
  MyProfileStyled,
  Title,
  ProfileSection,
  GroupButtonStyled,
  MyProfileValidationErrorMessage
} from './MyProfile.styled'
import { Col, Form, Row } from 'react-bootstrap'
import { BreadcrumbMenu } from 'layouts/BreadcrumbMenu'
import { CancelButton, SubmitButton } from './ContactUs.styled'
import { LinkBaseStyled } from 'layouts/LoginLayout.styled'
import { FormattedMessage } from 'react-intl'
import { MultiLanguageCode } from 'common/languageCodes'
import { useGlobalStore } from 'store/useGlobalStore'
import { shallow } from 'zustand/shallow'
import { HTMLText } from 'components/Share/HtmlText'
import { useFetch } from 'hooks/useFetch'
import {
  getGateReleaseNotificationEmails,
  getProfile,
  getPurchaseNotificationEmails,
  getSecretQuestions,
  saveProfile
} from 'apis/userApis'
import { useEffect, useState } from 'react'
import { FormInputStyled } from 'components/MyProfile/FormInput.styled'
import { FieldArray, Formik, FormikErrors } from 'formik'
import * as Yup from 'yup'
import { IMyProfile, ISecretAnswer, ISaveProfile } from 'types/userTypes'
import { EmailConfirmation } from 'components/MyProfile/EmailConfirmation'
import { ValidationErrorMessage } from 'components/Forms'
import { OverlayLoader } from 'components/Loader'
import { FormInput } from 'components/Forms'
import { DismissableAlert } from 'components/DismissableAlert/DismissableAlert'
import { setPageInfo } from 'utils/analyticsUtils'

interface IMessage {
  isSuccess: boolean
  message: string
}

declare module 'yup' {
  interface ObjectSchema<TIn, TContext, TDefault, TFlags> {
    uniqueProperty(propertyName: string, message: string): this
  }
}

export const MyProfile = () => {
  const [userClaims] = useGlobalStore((state) => [state.userClaims, state.getLocalText, state.vendor], shallow)
  const { data: userProfile } = useFetch(getProfile)
  const { data: secretQuestions } = useFetch(() => getSecretQuestions())
  const { data: purchaseNotificationEmails } = useFetch(() => getPurchaseNotificationEmails())
  const { data: gateReleaseNotificationEmails } = useFetch(() => getGateReleaseNotificationEmails())

  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState<IMessage>({ isSuccess: true, message: '' })
  const passwordTooltipHeader = '<h4>Password Rules</h4>'

  // Initialize analytics for this page
  useEffect(() => {
    setPageInfo('My Profile')
  }, [])

  // Reusable email validation function to avoid duplication
  const createUniqueEmailValidation = () =>
    Yup.string()
      .email('Invalid Email Address')
      .required('Enter Email Address')
      .test('unique-in-array', 'Email already exists', function (value) {
        if (!value || value.trim() === '') return true
        const { parent, path } = this
        const currentIndex = parseInt(path.split('[')[1]?.split(']')[0])
        const allEmails = parent || []

        // Find the first occurrence of this email
        const firstOccurrenceIndex = allEmails.findIndex((email) => email === value)

        // Only show error if this is NOT the first occurrence
        return firstOccurrenceIndex === currentIndex
      })

  Yup.addMethod(Yup.ObjectSchema, 'uniqueProperty', function (propertyName: string, message: string) {
    return this.test('unique', message, function (value: any) {
      if (!value || !value[propertyName]) {
        return true
      }

      const { path } = this
      const options = [...this.parent]

      const subsOptions = options.filter((option: any) => {
        if (option[propertyName] === value[propertyName]) {
          return true
        }
      })

      if (subsOptions.length > 1) {
        throw this.createError({
          path: `${path}.${propertyName}`,
          message
        })
      }

      return true
    })
  })

  const validationSchema = Yup.object({
    CurrentPassword: Yup.string().trim(),
    NewPassword: Yup.string()
      .trim()
      .test('required-when-current-password', 'Required', function (value) {
        const { CurrentPassword } = this.parent
        if (CurrentPassword && CurrentPassword.trim().length > 0) {
          return value && value.trim().length > 0
        }
        return true
      })
      .matches(
        /^(?=.*[0-9])(?=.{8,})/,
        'Must Contain At Least 8 Characters, One Number And Should Contain One Uppercase, One Lowercase, One Special Case Character'
      ),
    ConfirmPassword: Yup.string()
      .trim()
      .test('confirm-password-required', 'Required', function (value) {
        const { NewPassword } = this.parent
        if (NewPassword && NewPassword.trim().length > 0) {
          return value && value.trim().length > 0
        }
        return true
      })
      .test('confirm-password-match', 'Passwords do not match', (value, ctx) => {
        const { NewPassword } = ctx.parent
        if (!NewPassword || NewPassword.trim().length === 0 || !value || value.trim().length === 0) {
          return true
        }
        return NewPassword === value
      }),
    EmailAddress: Yup.string().trim().email('Invalid Email Address').required('Enter Email Address'),
    SecretAnswers: Yup.array().of(
      Yup.object()
        .shape({
          Answer: Yup.string().required('Required'),
          Question: Yup.string()
        })
        .uniqueProperty('Question', 'You must select two different secret questions')
    ),
    PurchaseEmails: Yup.array().of(createUniqueEmailValidation()),
    SoldEmails: Yup.array().of(createUniqueEmailValidation())
  })

  const handleSubmit = async (values: IMyProfile, { setFieldValue }: any) => {
    setIsLoading(true)
    setMessage({ isSuccess: true, message: '' })

    try {
      const newProfile: ISaveProfile = {
        OldPassword: values.CurrentPassword,
        NewPassword: values.NewPassword,
        SecretQuestion1Id: values.SecretAnswers[0].QuestionId,
        SecretQuestion1Answer: values.SecretAnswers[0].Answer,
        SecretQuestion2Id: values.SecretAnswers[1].QuestionId,
        SecretQuestion2Answer: values.SecretAnswers[1].Answer,
        EmailAddress: values.EmailAddress,
        PurchaseNotificationEmail: values.PurchaseEmails.join(';'),
        GateReleaseNotificationEmail: values.SoldEmails.join(';')
      }

      const result = await saveProfile(newProfile)
      if (result.Success) {
        setMessage({ isSuccess: true, message: result.Value || 'Profile updated successfully!' })
        setFieldValue('CurrentPassword', '')
        setFieldValue('NewPassword', '')
        setFieldValue('ConfirmPassword', '')
      } else {
        const errorMessage =
          result.ErrorMessages.length > 0
            ? result.ErrorMessages.join('. ')
            : 'An error occurred while updating your profile'
        setMessage({ isSuccess: false, message: errorMessage })
      }
    } catch (error: any) {
      // Check if this is our API error response (thrown by fetchAsync when !response.ok)
      if (error?.Success === false && error?.ErrorMessages) {
        const errorMessage =
          error.ErrorMessages.length > 0
            ? error.ErrorMessages.join('. ')
            : 'An error occurred while updating your profile'
        setMessage({ isSuccess: false, message: errorMessage })
      } else {
        const errorMessage = error?.message || 'Network error occurred. Please try again.'
        setMessage({ isSuccess: false, message: errorMessage })
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Layout title="My Profile">
      <MyProfileStyled>
        <Row>
          <Col>
            <BreadcrumbMenu />
          </Col>
        </Row>
        <Row>
          <Col>
            <Title>
              <HTMLText value="MY PROFILE" />
            </Title>
          </Col>
        </Row>
        {isLoading && <OverlayLoader />}
        <Formik
          initialValues={
            {
              SecretAnswers: userProfile?.SecretAnswers || [],
              EmailAddress: userProfile?.EmailAddress,
              CurrentPassword: '',
              NewPassword: '',
              ConfirmPassword: '',
              PurchaseEmails: purchaseNotificationEmails ? purchaseNotificationEmails?.split(';') : [],
              SoldEmails: gateReleaseNotificationEmails ? gateReleaseNotificationEmails?.split(';') : []
            } as IMyProfile
          }
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          validateOnBlur
          enableReinitialize={true}
        >
          {(formik) => (
            <Form noValidate onSubmit={formik.handleSubmit} autoComplete="off">
              <MyProfileBody>
                <SectionTitle>CHANGE PASSWORD</SectionTitle>
                <ProfileSection>
                  <FormInput
                    className="form-group label input-group col-sm-6"
                    name="CurrentPassword"
                    value={formik.values?.CurrentPassword}
                    type="password"
                    label="CURRENT PASSWORD"
                    onChange={(e) => {
                      formik.handleChange(e)
                      if (formik.touched.NewPassword) {
                        formik.setFieldTouched('NewPassword', false)
                        formik.setFieldError('NewPassword', undefined)
                      }
                    }}
                    onBlur={formik.handleBlur}
                    isInvalid={!!formik.errors.CurrentPassword && formik.touched.CurrentPassword}
                    error={formik.errors.CurrentPassword}
                    autoComplete="nope"
                  />
                  <FormInput
                    className="form-group label input-group col-sm-6"
                    name="NewPassword"
                    value={formik.values?.NewPassword}
                    type="password"
                    label="NEW PASSWORD"
                    infoIcon={userProfile ? passwordTooltipHeader + userProfile?.RuleList : ''}
                    onChange={(e) => {
                      formik.handleChange(e)
                      if (formik.touched.ConfirmPassword) {
                        formik.setFieldTouched('ConfirmPassword', false)
                        formik.setFieldError('ConfirmPassword', undefined)
                      }
                    }}
                    onBlur={formik.handleBlur}
                    isInvalid={!!formik.errors.NewPassword && formik.touched.NewPassword}
                    autoComplete="new-password"
                    isChangeEyeIcon={!!formik.errors.NewPassword && formik.touched.NewPassword}
                  />
                  {!!formik.errors.NewPassword && formik.touched.NewPassword && (
                    <ValidationErrorMessage className="form-text">{formik.errors.NewPassword}</ValidationErrorMessage>
                  )}
                  <FormInput
                    className="form-group label input-group col-sm-6"
                    name="ConfirmPassword"
                    value={formik.values?.ConfirmPassword}
                    type="password"
                    label="CONFIRM PASSWORD"
                    onChange={formik.handleChange}
                    onBlur={(e) => {
                      formik.setFieldTouched('ConfirmPassword', true)
                      formik.validateField('ConfirmPassword')
                    }}
                    isInvalid={!!formik.errors.ConfirmPassword && formik.touched.ConfirmPassword}
                    autoComplete="new-password"
                    isChangeEyeIcon={!!formik.errors.ConfirmPassword && formik.touched.ConfirmPassword}
                  />
                  {!!formik.errors.ConfirmPassword && formik.touched.ConfirmPassword && (
                    <ValidationErrorMessage className="form-text">
                      {formik.errors.ConfirmPassword}
                    </ValidationErrorMessage>
                  )}
                </ProfileSection>
                <SectionTitle>SECRET QUESTIONS</SectionTitle>
                <ProfileSection>
                  <FieldArray
                    name="SecretAnswers"
                    render={(arrayHelper) => (
                      <>
                        <Form.Row>
                          {formik.values?.SecretAnswers?.map((question, index) => (
                            <div className="col-sm-6">
                              <FormInputStyled key={`question-${question.QuestionId}`}>
                                <Form.Label className="label">SECRET QUESTION {index + 1}</Form.Label>
                                <Form.Control
                                  name={`SecretAnswers[${index}].Question`}
                                  as="select"
                                  value={question?.Question}
                                  onChange={(e) => {
                                    const selectedQuestion = secretQuestions?.find((q) => q.Question === e.target.value)
                                    if (selectedQuestion) {
                                      formik.setFieldValue(
                                        `SecretAnswers[${index}].Question`,
                                        selectedQuestion.Question
                                      )
                                      formik.setFieldValue(`SecretAnswers[${index}].QuestionId`, selectedQuestion.ID)
                                    }
                                  }}
                                  onBlur={formik.handleBlur}
                                  isInvalid={
                                    !!(formik?.errors?.SecretAnswers?.[index] as FormikErrors<ISecretAnswer>)?.Question
                                  }
                                  custom
                                  required
                                >
                                  {secretQuestions?.map((question) => {
                                    return (
                                      <option key={question.ID} value={question.Question}>
                                        {question.Question}
                                      </option>
                                    )
                                  })}
                                </Form.Control>
                                {(formik?.errors?.SecretAnswers?.[index] as FormikErrors<ISecretAnswer>)?.Question && (
                                  <MyProfileValidationErrorMessage>
                                    {(formik?.errors?.SecretAnswers?.[index] as FormikErrors<ISecretAnswer>)?.Question}
                                  </MyProfileValidationErrorMessage>
                                )}
                              </FormInputStyled>
                              <FormInput
                                className="input-field"
                                key={`answer-${question.QuestionId}`}
                                name={`SecretAnswers[${index}].Answer`}
                                type="text"
                                label="PLEASE ENTER THE ANSWER TO YOUR SECRET QUESTION"
                                value={question?.Answer}
                                onChange={(event) => {
                                  formik.setFieldValue(`SecretAnswers[${index}].Answer`, event.target.value)
                                }}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  !!(formik?.errors?.SecretAnswers?.[index] as FormikErrors<ISecretAnswer>)?.Answer &&
                                  formik?.touched?.SecretAnswers?.[index]?.Answer
                                }
                                error={(formik?.errors?.SecretAnswers?.[index] as FormikErrors<ISecretAnswer>)?.Answer}
                              />
                            </div>
                          ))}
                        </Form.Row>
                      </>
                    )}
                  />
                </ProfileSection>
                <SectionTitle>CHANGE EMAIL ADDRESS</SectionTitle>
                <ProfileSection>
                  <FormInput
                    className="form-group label input-field  col-sm-6"
                    name="EmailAddress"
                    type="text"
                    label="EMAIL ADDRESS"
                    value={formik.values?.EmailAddress}
                    onChange={(e) => {
                      formik.setFieldValue('EmailAddress', e.target.value.slice(0, 50))
                    }}
                    onBlur={formik.handleBlur}
                    isInvalid={!!formik.errors.EmailAddress && formik.touched.EmailAddress}
                    error={formik.errors.EmailAddress}
                    autoComplete="nope"
                    data-lpignore="true"
                    maxLength={50}
                  />
                </ProfileSection>
                <SectionTitle>ADD ADDITIONAL EMAIL NOTIFICATIONS</SectionTitle>
                <ProfileSection>
                  <EmailConfirmation name="PurchaseEmails" label="PURCHASE CONFIRMATION" />
                  <EmailConfirmation name="SoldEmails" label="BILL OF SALE CONFIRMATION" />
                  <Form.Row className="text-right">
                    <GroupButtonStyled>
                      <CancelButton>
                        <LinkBaseStyled to={!userClaims.Username ? '/login' : '/'}>
                          <FormattedMessage id={MultiLanguageCode.CANCEL_BUTTON} />
                        </LinkBaseStyled>
                      </CancelButton>
                      <SubmitButton type="submit" className="submit-button">
                        <FormattedMessage id={MultiLanguageCode.SUBMIT} />
                      </SubmitButton>
                    </GroupButtonStyled>
                  </Form.Row>

                  {/* Error/Success Message Display */}
                  {message?.message?.length > 0 && (
                    <Form.Row className="mt-3">
                      <Col xs={12}>
                        <DismissableAlert
                          variant={message.isSuccess ? 'success' : 'danger'}
                          message={message.message}
                          onClose={() => setMessage({ isSuccess: true, message: '' })}
                          autoDismissable={message.isSuccess ? 5000 : 0} // Auto-dismiss success, keep errors
                        />
                      </Col>
                    </Form.Row>
                  )}
                </ProfileSection>
              </MyProfileBody>
            </Form>
          )}
        </Formik>
      </MyProfileStyled>
    </Layout>
  )
}
